
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ShoppingCart, Heart } from "lucide-react";
import { ProductRecommendation } from "@/types/product";
import { generateProductUrl, handleProductNavigation } from "@/utils/productUrlUtils";
import { useNavigate } from "react-router-dom";

interface ProductRecommendationsSectionProps {
  recommendations: ProductRecommendation[];
  type: 'related' | 'frequently_bought_together' | 'similar';
}

const ProductRecommendationsSection = ({
  recommendations,
  type
}: ProductRecommendationsSectionProps) => {
  const navigate = useNavigate();
  const [visibleCount, setVisibleCount] = useState(4);

  const filteredRecommendations = recommendations.filter(
    rec => rec.recommendation_type === type
  );

  if (!filteredRecommendations || filteredRecommendations.length === 0) {
    return null;
  }

  const displayedRecommendations = filteredRecommendations.slice(0, visibleCount);

  const getTitle = () => {
    switch (type) {
      case 'related':
        return 'Related Products';
      case 'frequently_bought_together':
        return 'Frequently Bought Together';
      case 'similar':
        return 'Similar Products';
      default:
        return 'Recommended Products';
    }
  };

  const handleProductClick = (product: any) => {
    if (product) {
      handleProductNavigation(product, navigate);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{getTitle()}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {displayedRecommendations.map((recommendation) => (
            <div
              key={recommendation.id}
              className="group cursor-pointer bg-card border rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
              onClick={() => handleProductClick(recommendation.recommended_product)}
            >
              <div className="aspect-square bg-muted relative overflow-hidden">
                {recommendation.recommended_product?.image_url ? (
                  <img
                    src={recommendation.recommended_product.image_url}
                    alt={recommendation.recommended_product.name || 'Product'}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-muted">
                    <span className="text-muted-foreground">No Image</span>
                  </div>
                )}
                <Badge className="absolute top-2 left-2 bg-primary/90">
                  {Math.round(recommendation.score * 100)}% match
                </Badge>
              </div>
              
              <div className="p-4 space-y-3">
                <h4 className="font-semibold text-foreground line-clamp-2 group-hover:text-primary transition-colors">
                  {recommendation.recommended_product?.name || 'Product Name'}
                </h4>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <div className="text-lg font-bold text-primary">
                      ${recommendation.recommended_product?.price?.toFixed(2) || '0.00'}
                    </div>
                    {recommendation.recommended_product?.compare_at_price && (
                      <div className="text-sm text-muted-foreground line-through">
                        ${recommendation.recommended_product.compare_at_price.toFixed(2)}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline">
                      <Heart className="h-4 w-4" />
                    </Button>
                    <Button size="sm">
                      <ShoppingCart className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredRecommendations.length > visibleCount && (
          <div className="mt-6 text-center">
            <Button
              variant="outline"
              onClick={() => setVisibleCount(prev => prev + 4)}
            >
              Load More Products
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ProductRecommendationsSection;
